import React, { useEffect, useState, useRef, useContext } from "react";
import styles from "./ManageUsers.module.css";
import { getPageFromName } from "../../route_utils";
import { useNavigate } from "react-router-dom";
import api_endpoints from "../../api";
import AuthContext from "../../context/AuthContext/AuthContext";
import { numberWithCommas } from "../../utils";
import LoadingContainer from "../LoadingContainer/LoadingContainer";
import Chip from "@mui/material/Chip";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Switch,
} from "@mui/material";
import { NavigationButton } from "../../widgets/NavigationButton/NavigationButton";
import DataGrid from "../../widgets/DataGrid/DataGrid";
import CustomTextField from "../../widgets/CustomTextField/CustomTextField";
import CustomSelect from "../../widgets/CustomSelect/CustomSelect";
import CustomDatePicker from "../../widgets/CustomDatePicker/CustomDatePicker";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import axios from "axios";
import dayjs from "dayjs";
import { useCustomTranslation } from "../../hooks/useCustomTranslation";
import { useSuccessAlert } from "../../context/SuccessAlertContext/SuccessAlertContext";

const ManageUsers = () => {
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const [usersCount, setUsersCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [OTPCode, setOTPCode] = useState("");
  const [rows, setRows] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const cancelTokenSource = useRef(null);
  const { t } = useCustomTranslation("Utilisateurs");
  const { setShowSuccessAlert, setMessageSuccessAlert } = useSuccessAlert();

  const currentUserId = authContext.authContext.accessToken.id;

  const [tableFilter, setTableFilter] = useState({
    first_name: "",
    last_name: "",
    email: "",
    created_at: "",
    is_verified: null,
    is_active: true,
    is_deleted: false,
  });

  const openViewUser = (row) => {
    navigate(`${getPageFromName("Voir l'utilisateur").path}?user_id=${row.id}`, {
      state: { user_id: row.id },
    });
  };

  const openEditUser = (row) => {
    navigate(getPageFromName("Paramètres utilisateur").path, {
      state: { user_id: row.id },
    });
  };

  const openDeleteDialog = (row) => {
    setSelectedUser(row);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedUser(null);
  };

  const showUserOTP = async (row) => {
    if (row.is_verified === false) {
      try {
        let otpResponse = await api_endpoints.checkUserOTP(row.email);
        if (otpResponse.status === 200) {
          setOTPCode(otpResponse.data.otp);
          setModalOpen(true);
        }
      } catch (e) {
        console.error(e);
      }
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    setActionLoadingId(selectedUser.id);

    try {
      const response = await api_endpoints.deleteUser(selectedUser.id);

      if (response.status === 200) {
        setMessageSuccessAlert(
          response?.data?.message || t("user_delete_success", { defaultValue: "Utilisateur supprimé avec succès" })
        );
        setShowSuccessAlert(true);
        closeDeleteDialog();
        getData();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleRestoreUser = async (row) => {
    setActionLoadingId(row.id);

    try {
      const response = await api_endpoints.restoreUser(row.id);

      if (response.status === 200) {
        setMessageSuccessAlert(
          response?.data?.message || t("user_restore_success", { defaultValue: "Utilisateur restauré avec succès" })
        );
        setShowSuccessAlert(true);
        getData();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleToggleActive = async (row) => {
    setActionLoadingId(row.id);

    try {
      const response = await api_endpoints.toggleUserActive(row.id);

      if (response.status === 200) {
        setMessageSuccessAlert(
          response?.data?.message ||
            (row.is_active
              ? t("user_deactivate_success", { defaultValue: "Utilisateur désactivé avec succès" })
              : t("user_activate_success", { defaultValue: "Utilisateur activé avec succès" }))
        );
        setShowSuccessAlert(true);
        getData();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setActionLoadingId(null);
    }
  };

  const columns = [
    {
      field: "first_name",
      headerName: t("user_table_firstname"),
      sortable: true,
      numberSort: false,
    },
    {
      field: "last_name",
      headerName: t("user_table_lastname"),
      sortable: true,
      numberSort: false,
    },
    {
      field: "email",
      headerName: t("user_table_email"),
      sortable: true,
      numberSort: false,
    },
    {
      field: "created_at",
      headerName: t("user_table_creationdate"),
      sortable: true,
      numberSort: false,
      renderCell: (params) => dayjs(params.value).format("YYYY-MM-DD"),
    },
    {
      field: "is_verified",
      headerName: t("user_table_verified_filter"),
      sortable: true,
      numberSort: false,
      renderCell: (params) => (
        <Chip
          label={
            params.value
              ? t("user_table_verified")
              : t("user_table_not_verified")
          }
          color={params.value ? "success" : "error"}
          variant="contained"
        />
      ),
    },
    {
      field: "is_active",
      headerName: t("user_table_status"),
      sortable: true,
      numberSort: false,
      renderCell: (params) => (
        <div
          className="flex items-center space-x-2"
          onClick={(e) => e.stopPropagation()}
        >
          <Switch
            checked={Boolean(params.row.is_active)}
            disabled={
              params.row.is_deleted ||
              params.row.is_owner ||
              actionLoadingId === params.row.id
            }
            onChange={() => handleToggleActive(params.row)}
          />
          <span>
            {params.row.is_active
              ? t("user_table_active")
              : t("user_table_not_active")}
          </span>
        </div>
      ),
    },
    {
      field: "actions",
      headerName: t("user_table_actions", { defaultValue: "Actions" }),
      sortable: false,
      numberSort: false,
      renderCell: (params) => (
        <div
          className="flex items-center space-x-1"
          onClick={(e) => e.stopPropagation()}
        >
          <Tooltip title={t("user_action_view", { defaultValue: "Voir" })}>
            <IconButton size="small" onClick={() => openViewUser(params.row)}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {!params.row.is_deleted && (
            <Tooltip title={t("user_action_edit", { defaultValue: "Modifier" })}>
              <span>
                <IconButton
                  size="small"
                  disabled={actionLoadingId === params.row.id}
                  onClick={() => openEditUser(params.row)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}

          {!params.row.is_deleted ? (
            <Tooltip title={t("user_action_delete", { defaultValue: "Supprimer" })}>
              <span>
                <IconButton
                  size="small"
                  disabled={
                    params.row.is_owner ||
                    params.row.id === currentUserId ||
                    actionLoadingId === params.row.id
                  }
                  onClick={() => openDeleteDialog(params.row)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <Tooltip title={t("user_action_restore", { defaultValue: "Restaurer" })}>
              <span>
                <IconButton
                  size="small"
                  disabled={actionLoadingId === params.row.id}
                  onClick={() => handleRestoreUser(params.row)}
                >
                  <RestoreFromTrashIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}

          {!params.row.is_verified && !params.row.is_deleted && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => showUserOTP(params.row)}
            >
              OTP
            </Button>
          )}
        </div>
      ),
    },
  ];

  const filters = {
    first_name: (
      <CustomTextField
        value={tableFilter.first_name || ""}
        onChange={(e) => changeTableFilter("first_name", e.target.value)}
        clearFilter={() => clearTableFilter("first_name")}
      />
    ),
    last_name: (
      <CustomTextField
        value={tableFilter.last_name || ""}
        onChange={(e) => changeTableFilter("last_name", e.target.value)}
        clearFilter={() => clearTableFilter("last_name")}
      />
    ),
    email: (
      <CustomTextField
        value={tableFilter.email || ""}
        onChange={(e) => changeTableFilter("email", e.target.value)}
        clearFilter={() => clearTableFilter("email")}
      />
    ),
    created_at: (
      <CustomDatePicker
        value={tableFilter.created_at}
        onChange={(newValue) => changeTableFilter("created_at", newValue)}
        clearFilter={() => clearTableFilter("created_at")}
      />
    ),
    is_verified: (
      <CustomSelect
        value={tableFilter.is_verified}
        onChange={(e) => {
          changeTableFilter("is_verified", e.target.value);
        }}
        options={[
          { value: null, label: t("user_table_all_option") },
          { value: true, label: t("user_table_yes_option") },
          { value: false, label: t("user_table_no_option") },
        ]}
      />
    ),

    is_active: (
      <CustomSelect
        value={tableFilter.is_active}
        onChange={(e) => {
          changeTableFilter("is_active", e.target.value);
        }}
        options={[
          { value: null, label: t("user_table_all_option") },
          { value: true, label: t("user_table_active") },
          { value: false, label: t("user_table_not_active") },
        ]}
      />
    ),

    actions: (
      <CustomSelect
        value={tableFilter.is_deleted}
        onChange={(e) => {
          changeTableFilter("is_deleted", e.target.value);
        }}
        options={[
          { value: false, label: t("user_filter_active_only", { defaultValue: "Actifs" }) },
          { value: true, label: t("user_filter_deleted_only", { defaultValue: "Supprimés" }) },
        ]}
      />
    ),
  };

  useEffect(() => {
    getData();
    return () => {
      if (cancelTokenSource.current) {
        cancelTokenSource.current.cancel(
          "Operation canceled due to component unmount."
        );
      }
    };
  }, [tableFilter, authContext.authContext.accessToken.organization_id]);

  const getData = async () => {
    setLoading(true);
    setLoadingError(false);

    if (cancelTokenSource.current) {
      cancelTokenSource.current.cancel(
        "Operation canceled due to new request."
      );
    }

    cancelTokenSource.current = axios.CancelToken.source();

    try {
      const data = await fetchUsers(cancelTokenSource.current.token);
      setRows(data);
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log("Request canceled", error.message);
      } else {
        console.error(error);
        setLoadingError(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const clearTableFilter = (option) => {
    setTableFilter((prevFilter) => ({ ...prevFilter, [option]: "" }));
  };

  const fetchUsers = async () => {
    setLoading(true);

    try {
      const created_at = formatDate(tableFilter.created_at);

      let response = await api_endpoints.getUsers(
        authContext.authContext.accessToken.organization_id,
        tableFilter.first_name,
        tableFilter.last_name,
        tableFilter.email,
        created_at,
        tableFilter.is_active,
        tableFilter.is_verified,
        undefined,
        tableFilter.is_deleted
      );

      if (response.status === 200) {
        setUsersCount(response.data.usersCount);

        return response.data.users.map((user) => ({
          id: user._id,
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
          created_at: user.created_at,
          is_verified: user.is_verified,
          is_active: user.is_active,
          is_owner: user.is_owner,
          is_deleted: user.is_deleted,
          has_kiosk_code: user.has_kiosk_code,
        }));
      }
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  const selectUser = (params) => {
    openViewUser(params.row);
  };

  const changeTableFilter = (option, value) => {
    setTableFilter({ ...tableFilter, [option]: value });
  };

  return (
    <>
      <Dialog
        sx={{ "& .MuiDialog-paper": { maxHeight: 435 } }}
        maxWidth="sm"
        open={modalOpen}
      >
        <DialogTitle>{t("user_otp_dialog_heading")}</DialogTitle>
        <DialogContent>
          <div className="flex flex-1 flex-col space-y-4">
            <h1>{t("user_otp_message")}</h1>
            <strong className="w-full text-center text-7xl font-extrabold">
              {OTPCode}
            </strong>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} variant="contained">
            {t("user_otp_close_button")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{t("user_delete_dialog_title", { defaultValue: "Supprimer l'utilisateur" })}</DialogTitle>
        <DialogContent>
          {t("user_delete_dialog_message", { defaultValue: "Êtes-vous sûr de vouloir supprimer cet utilisateur ?" })}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>{t("user_delete_dialog_cancel", { defaultValue: "Annuler" })}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteUser}
            disabled={!selectedUser || actionLoadingId === selectedUser?.id}
          >
            {t("user_delete_dialog_confirm", { defaultValue: "Supprimer" })}
          </Button>
        </DialogActions>
      </Dialog>

      <div className={"flex justify-end"}>
        <NavigationButton
          startIcon={<AddIcon />}
          text={t("user_button_create_user")}
          path={getPageFromName("Créer un utilisateur").path}
        />
      </div>

      <div className={`${styles.header} mt-4`}>
        <div className={styles.left_header}>
          {loading ? (
            t("user_page_loading")
          ) : (
            <>
              {t("user_page_number_of_users", {
                count: numberWithCommas(usersCount),
              })}
            </>
          )}
        </div>
      </div>

      <div>
        <div className="flex flex-1 h-full mt-5 flex-col">
          <LoadingContainer loading={loading} error={loadingError}>
            <DataGrid
              columns={columns}
              rows={rows}
              showDetails={selectUser}
              filters={filters}
              loading={loading}
            />
          </LoadingContainer>
        </div>
      </div>
    </>
  );
};

export default ManageUsers;